import { createBrowserRouter } from 'react-router-dom';
import { ProtectedRoute, RequireCapability } from '@nexdel/core/auth';
import { AppLayout } from '@nexdel/ui/layout/AppLayout';
import { MfaGate } from '@nexdel/ui/layout/MfaGate';
import { lazy, Suspense } from 'react';

// Public pages — loaded eagerly (small)
import Login from '../pages/Login';
import ForgotPassword from '../pages/ForgotPassword';
import ResetPassword from '../pages/ResetPassword';
import AcceptInvite from '../pages/AcceptInvite';
import ExternalFeedback from '../pages/ExternalFeedback';
import InternalFeedback from '../pages/InternalFeedback';
import NotFound from '../pages/NotFound';

// Protected pages — lazy-loaded
const Dashboard = lazy(() => import('../pages/Dashboard'));
const Mandates = lazy(() => import('../pages/Mandates'));
const MandateDetail = lazy(() => import('../pages/MandateDetail'));
const Requirements = lazy(() => import('../pages/Requirements'));
const RequirementDetail = lazy(() => import('../pages/RequirementDetail'));
const Suppliers = lazy(() => import('../pages/Suppliers'));
const SupplierDetail = lazy(() => import('../pages/SupplierDetail'));
const Contacts = lazy(() => import('../pages/Contacts'));
const ContactDetail = lazy(() => import('../pages/ContactDetail'));
const Settings = lazy(() => import('../pages/Settings'));
const Notifications = lazy(() => import('../pages/Notifications'));
const Profile = lazy(() => import('../pages/Profile'));
const DataManagement = lazy(() => import('../pages/DataManagement'));
const Submissions = lazy(() => import('../pages/Submissions'));
const SupplierAnalytics = lazy(() => import('../pages/SupplierAnalytics'));
const DeliverableAnalytics = lazy(() => import('../pages/DeliverableAnalytics'));

const fallback = <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">Loading…</div>;

export const router = createBrowserRouter([
  // Public routes
  { path: '/login', element: <Login /> },
  { path: '/forgot-password', element: <ForgotPassword /> },
  { path: '/reset-password', element: <ResetPassword /> },
  { path: '/accept-invite', element: <AcceptInvite /> },
  { path: '/external/feedback/:token', element: <ExternalFeedback /> },
  { path: '/feedback/:participantId', element: <InternalFeedback /> },

  // Protected routes — all rendered inside the app shell
  {
    element: (
      <ProtectedRoute>
        <MfaGate>
          <AppLayout />
        </MfaGate>
      </ProtectedRoute>
    ),
    children: [
      { path: '/', element: <Suspense fallback={fallback}><Dashboard /></Suspense> },
      { path: '/dashboard', element: <Suspense fallback={fallback}><Dashboard /></Suspense> },
      { path: '/mandates', element: <Suspense fallback={fallback}><Mandates /></Suspense> },
      { path: '/mandates/:id', element: <Suspense fallback={fallback}><MandateDetail /></Suspense> },
      { path: '/requirements', element: <Suspense fallback={fallback}><Requirements /></Suspense> },
      { path: '/requirements/:id', element: <Suspense fallback={fallback}><RequirementDetail /></Suspense> },
      { path: '/submissions', element: <Suspense fallback={fallback}><Submissions /></Suspense> },
      { path: '/suppliers', element: <RequireCapability capability="VIEW_ALL"><Suspense fallback={fallback}><Suppliers /></Suspense></RequireCapability> },
      { path: '/suppliers/:id', element: <RequireCapability capability="VIEW_ALL"><Suspense fallback={fallback}><SupplierDetail /></Suspense></RequireCapability> },
      { path: '/contacts', element: <RequireCapability capability="VIEW_ALL"><Suspense fallback={fallback}><Contacts /></Suspense></RequireCapability> },
      { path: '/contacts/:id', element: <RequireCapability capability="VIEW_ALL"><Suspense fallback={fallback}><ContactDetail /></Suspense></RequireCapability> },
      { path: '/settings/*', element: <RequireCapability capability="MANAGE_ORG"><Suspense fallback={fallback}><Settings /></Suspense></RequireCapability> },
      { path: '/notifications', element: <Suspense fallback={fallback}><Notifications /></Suspense> },
      { path: '/profile', element: <Suspense fallback={fallback}><Profile /></Suspense> },
      { path: '/data', element: <RequireCapability capability="MANAGE_ORG"><Suspense fallback={fallback}><DataManagement /></Suspense></RequireCapability> },
      { path: '/supplier-analytics', element: <RequireCapability capability="VIEW_ALL"><Suspense fallback={fallback}><SupplierAnalytics /></Suspense></RequireCapability> },
      { path: '/deliverable-analytics', element: <RequireCapability capability="VIEW_ALL"><Suspense fallback={fallback}><DeliverableAnalytics /></Suspense></RequireCapability> },
    ],
  },

  // Catch-all
  { path: '*', element: <NotFound /> },
]);
